export const profileFields = ["organization", "jobTitle", "document", "birthDate", "source"];

const cleanText = value => typeof value === "string" ? value.trim().replace(/\s+/g, " ") : "";
const phoneDigits = value => {
  const text = cleanText(value);
  const jid = text.match(/^(\d+)(?::\d+)?@(s\.whatsapp\.net|c\.us)$/i);
  if (!jid && !/^\+?[\d\s().-]+$/.test(text)) return "";
  const digits = jid ? jid[1] : text.replace(/\D/g, "");
  return /^[1-9]\d{7,14}$/.test(digits) ? digits : "";
};
const formatPhone = (digits, hideNumber) => {
  if (!digits) return "";
  const visible = hideNumber ? `${digits.slice(0, -6)}****${digits.slice(-2)}` : digits;
  if (digits.startsWith("55") && [12, 13].includes(digits.length)) {
    return `+55 (${visible.slice(2, 4)}) ${visible.slice(4, -4)}-${visible.slice(-4)}`;
  }
  return `+${visible}`;
};

// Presentation only: an opaque WhatsApp LID must never become a telephone number.
export const contactPresentation = (contact = {}, hideNumber = false) => {
  const record = contact || {};
  const originalName = cleanText(record.name);
  const jidValues = [record.lidJid, record.lid, record.waJid, record.remoteJid, record.number, originalName];
  const lidDigits = jidValues.map(value => cleanText(value).match(/^(\d+)(?::\d+)?@lid$/i)).filter(Boolean).map(match => match[1]);
  [record.lidJid, record.lid].forEach(value => { if (/^\d+$/.test(cleanText(value))) lidDigits.push(cleanText(value)); });
  const hasLid = lidDigits.length > 0 || record.identitySource === "lid" || Boolean(record.lidJid || record.lid);
  const candidates = [record.phoneNumber, record.phoneJid,
    ...[record.waJid, record.remoteJid].filter(value => /@(s\.whatsapp\.net|c\.us)$/i.test(cleanText(value)))];
  if (!hasLid && record.identitySource !== "username" && !record.isGroup && (!record.channel || record.channel === "whatsapp")) candidates.push(record.number);
  const otherChannel = record.channel && record.channel !== "whatsapp";
  const digits = record.isGroup ? "" : candidates.map(phoneDigits).find(value => value && !lidDigits.includes(value)
    && !(otherChannel && record.identitySource !== "phone" && value === phoneDigits(record.number))) || "";
  const phone = formatPhone(digits, hideNumber);
  const email = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanText(record.email))
    && !/@(s\.whatsapp\.net|c\.us|g\.us)$/i.test(cleanText(record.email)) ? cleanText(record.email) : "";
  const hasName = Boolean(originalName && !/^\+?[\d\s().-]+$/.test(originalName)
    && !/@(lid|s\.whatsapp\.net|c\.us|g\.us|broadcast)$/i.test(originalName)
    && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(originalName)
    && originalName.toLocaleLowerCase("pt-BR") !== "contato sem nome");
  const name = hasName ? originalName : "Contato sem nome";
  const reference = /^[1-9]\d*$/.test(String(record.id || "")) ? `Cadastro #${record.id}` : "";
  const parts = [phone, email, !hasName ? reference : ""].filter(Boolean);
  const secondary = Array.from(new Set(parts.filter(value => value.toLocaleLowerCase("pt-BR") !== name.toLocaleLowerCase("pt-BR")))).join(" · ");
  const words = name.split(" ");
  const initials = hasName ? `${Array.from(words[0])[0]}${words.length > 1 ? Array.from(words[words.length - 1])[0] : ""}`.toLocaleUpperCase("pt-BR") : "?";
  return { name, secondary, phone, email, initials, hasName, reference };
};

export const profileDraft = profile => profileFields.reduce((draft, key) => {
  draft[key] = (profile && profile[key]) || "";
  return draft;
}, { birthdayOptOut: Boolean(profile && profile.birthdayOptOut) });

export const mergePages = (current, incoming, key = "id") => {
  const values = new Map(current.map(item => [item[key], item]));
  incoming.forEach(item => values.set(item[key], item));
  return Array.from(values.values());
};

export const formatDateTime = value => {
  if (!value || Number.isNaN(new Date(value).getTime())) return "Não informado";
  return new Intl.DateTimeFormat("pt-BR", {
    dateStyle: "short", timeStyle: "short", timeZone: "America/Sao_Paulo"
  }).format(new Date(value));
};

export const localDeadlineToISO = value => {
  const parsed = new Date(value);
  if (!value || Number.isNaN(parsed.getTime())) throw new Error("Informe uma data e hora válidas.");
  return parsed.toISOString();
};

export const errorMessage = error => {
  const status = error && error.response && error.response.status;
  if (status === 403) return "Seu usuário ou plano não permite acessar estas informações.";
  if (status === 404) return "Registro não encontrado ou indisponível para seu acesso.";
  if (status === 409) return "Este registro foi alterado por outra pessoa. Atualize os dados antes de tentar novamente.";
  if (status === 400 && typeof error.response.data?.error === "string") return error.response.data.error;
  return "Não foi possível concluir a operação. Verifique sua conexão e tente novamente.";
};
